import _capitalize from 'lodash/capitalize'
import i18n, { getLanguage } from 'frontcore/i18n'
import {
  CalendarModeOptions,
  DateRange,
  IInputDateUseCase,
  InputDateViewModel,
  UseCoreCalendarArgs,
} from 'frontcore/presentation/calendar/domain/types'
import { computed, onMounted, reactive, ref } from 'vue'
import { formattedDate } from 'frontcore/presentation/calendar/domain/helpers'
import { getWeekNumber } from 'frontcore/domain/dates/date-utils'

type useInputDateEmits = (e: 'input', selectedValue: InputDateViewModel['selectedDate']) => void

const useInputDate = ({
  value = ref(null),
  range = ref(false),
  nullable = ref(true),
  selectionMode = ref(CalendarModeOptions.SELECT_DAY),
} : UseCoreCalendarArgs = {},
emit: useInputDateEmits
): IInputDateUseCase => {

  const {tc} = i18n.global

  const getDefaultDateValue = (dateInfo: InputDateViewModel['selectedDate']) : Date | null => {
    if (range.value && Array.isArray(dateInfo) && dateInfo.length > 0 && dateInfo[0] instanceof Date) {
      const dateStartValue = new Date(dateInfo[0].getTime())
      dateStartValue.setHours(0, 0, 0, 0)
      return dateStartValue
    } else if (dateInfo instanceof Date) {
      return dateInfo ?? null
    }
    return null
  }

  const getDefaultEndDateValue = (dateInfo: InputDateViewModel['selectedDate']) : Date | null => {
    if (range.value && Array.isArray(dateInfo) && dateInfo.length > 1) {
      const dateEndValue = new Date(dateInfo[1].getTime())
      dateEndValue.setHours(0, 0, 0, 0)
      return dateEndValue
    }
    return null
  }

  const dateValue = computed(() => getDefaultDateValue(value.value) ?? new Date())

  const selectedDate = computed(() => {
    const date = new Date(dateValue.value.getTime())
    date.setHours(0, 0, 0, 0)
    return date
  })

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const formattedMonthDate = (date: Date | null) => {
    return date ? `${_capitalize(date.toLocaleString(getLanguage(), { month: 'long' }))} ${date.getFullYear()}` : ''
  }

  const formattedWeekDate = (startDate: Date | null) => {
    return startDate ? `${tc('common.messages.week', 1)} ${getWeekNumber(startDate)}, ${startDate.getFullYear()}` : ''
  }

  const formattedDayDate = (startDate: Date | null, endDate: Date | null, viewModelselectedDate: Nullable<Date> | DateRange) => {
    if (range.value && startDate || endDate) {
      return `${formattedDate(startDate) ?? ''} - ${formattedDate(endDate) ?? ''}`
    }
    if (!range.value && !(viewModelselectedDate instanceof Array)) {
      return formattedDate(viewModelselectedDate) ?? ''
    }
    return ''
  }

  const formattedDateValue = computed(() => {
    switch (selectionMode.value) {
    case CalendarModeOptions.SELECT_MONTH:
      return formattedMonthDate(viewmodel.startDate)
    case CalendarModeOptions.SELECT_WEEK:
      return formattedWeekDate(viewmodel.startDate)
    case CalendarModeOptions.SELECT_DAY:
      return formattedDayDate(viewmodel.startDate, viewmodel.endDate, viewmodel.selectedDate)
    case CalendarModeOptions.SELECT_YEAR:
      return `${viewmodel.startDate?.getFullYear()}`
    default:
      return ''
    }
  })

  const viewmodel: InputDateViewModel = reactive({
    endDate: computed(() => getDefaultEndDateValue(value.value) ),
    selectedDate: computed(() => nullable.value ? getDefaultDateValue(value.value) : selectedDate.value),
    startDate: computed(() => getDefaultDateValue(value.value)),
  })

  onMounted(() => {
    if (value.value === null && !nullable.value) {
      emit('input', viewmodel.selectedDate)
    }
  })

  return {
    formattedDateValue,
  }
}

export default useInputDate
